// 负责浏览器侧后台 API 调用，与 AdminView 和 Pages Functions 后台接口关联。
import type { FulfillmentStatus } from "../domain/orders";

export type AdminOrder = {
  id: string;
  customerEmail: string;
  shippingCountry: string;
  paymentStatus: string;
  fulfillmentStatus: FulfillmentStatus;
  currency: "USD";
  subtotalAmount: number;
  shippingAmount: number;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
  lines: AdminOrderLine[];
  paymentEvents: AdminPaymentEvent[];
};

export type AdminOrderLine = {
  productId: string;
  productName: string;
  size: string;
  quantity: number;
  unitPriceAmount: number;
  lineTotalAmount: number;
};

export type AdminPaymentEvent = {
  providerEventId: string;
  outcome: string;
  createdAt: string;
};

export type AdminProduct = {
  id: string;
  name: string;
  category: string;
  imageUrl: string;
  variants: AdminProductVariant[];
};

export type AdminProductVariant = {
  productId: string;
  size: string;
  color: string;
  priceAmount: number;
  stockQuantity: number;
  isActive: boolean;
};

type OrdersResponse = {
  orders?: unknown;
  order?: unknown;
  error?: unknown;
};

type ProductsResponse = {
  products?: unknown;
  error?: unknown;
};

// 拉取后台订单列表。
export const fetchAdminOrders = async (token: string): Promise<AdminOrder[]> => {
  const response = await requestAdminJson<OrdersResponse>(
    "/api/admin/orders",
    token,
    {
      method: "GET"
    }
  );

  if (!Array.isArray(response.orders)) {
    throw new Error("Admin orders response is missing orders.");
  }

  return response.orders as AdminOrder[];
};

// 更新订单履约状态。
export const updateAdminOrderFulfillment = async (
  token: string,
  orderId: string,
  fulfillmentStatus: FulfillmentStatus
): Promise<AdminOrder> => {
  const response = await requestAdminJson<OrdersResponse>(
    "/api/admin/orders",
    token,
    {
      method: "PATCH",
      body: JSON.stringify({
        orderId,
        fulfillmentStatus
      })
    }
  );

  if (!response.order) {
    throw new Error("Admin orders response is missing order.");
  }

  return response.order as AdminOrder;
};

// 拉取后台商品列表。
export const fetchAdminProducts = async (
  token: string
): Promise<AdminProduct[]> => {
  const response = await requestAdminJson<ProductsResponse>(
    "/api/admin/products",
    token,
    {
      method: "GET"
    }
  );

  if (!Array.isArray(response.products)) {
    throw new Error("Admin products response is missing products.");
  }

  return response.products as AdminProduct[];
};

// 更新商品变体库存和上下架状态。
export const updateAdminProductVariant = async (
  token: string,
  variant: AdminProductVariant
): Promise<AdminProduct[]> => {
  const response = await requestAdminJson<ProductsResponse>(
    "/api/admin/products",
    token,
    {
      method: "PATCH",
      body: JSON.stringify({
        productId: variant.productId,
        size: variant.size,
        stockQuantity: variant.stockQuantity,
        isActive: variant.isActive
      })
    }
  );

  if (!Array.isArray(response.products)) {
    throw new Error("Admin products response is missing products.");
  }

  return response.products as AdminProduct[];
};

// 发送带后台令牌的 JSON 请求。
const requestAdminJson = async <ResponseBody>(
  path: string,
  token: string,
  init: RequestInit
): Promise<ResponseBody> => {
  const headers = new Headers(init.headers);
  headers.set("x-admin-token", token);
  headers.set("content-type", "application/json");

  const response = await fetch(path, {
    ...init,
    headers
  });
  const payload = parseJson(await response.text());

  if (!response.ok) {
    throw new Error(readApiError(payload));
  }

  return payload as ResponseBody;
};

// 解析 JSON 响应。
const parseJson = (text: string): Record<string, unknown> => {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    throw new Error("Admin API response must be JSON.");
  }
};

// 读取 API 错误消息。
const readApiError = (payload: Record<string, unknown>): string => {
  if (typeof payload.error === "string" && payload.error.trim()) {
    return payload.error;
  }

  return "Admin API request failed.";
};
